import { Box, Divider, Grid, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';

// components imports
import { jsonPathToValue } from '../../utils/json';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import Typography from '../../components/Typography/Typography';
import QuickGlanceLoader from './TabLoader';
import useMetaData from '../../hooks/useMetaData';
import { generateRandomString } from '../../utils/string';
import TeamList from './TeamList';
import GridLabels from './GridLabels';
import { vehicleService } from '../../services/vehicles';
import { Link } from '../../components';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { setQuickGlanceData } from '../../redux/slices/vehicle';
import ExternalLink from '../../components/ExternalLink/ExternalLink';

// handling title
const getTitle = (type: string, value: any) => {
  if (type === 'Date') {
    return value && moment(value).format('MMM D, YYYY');
  }
  if (type === 'Boolean') {
    if (value === true) {
      return 'Yes';
    }
    if (value === false) {
      return 'No';
    }
    return value;
  }
  return value;
};

const QuickGlance = () => {
  // use hooks
  const metaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.vehicleDetailsTabs);
  const quickGlanceMetaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.vehicleTab);
  const { reservationId } = useParams<{ reservationId: string }>();
  const dispatch = useAppDispatch();

  // global states
  const { quickGlanceData, data } = useAppSelector((state) => state.vehicle);

  // handling links
  const handleLink = (name: string, replace: string, id?: string, url?: string) => {
    if (id) {
      return (
        <ExternalLink
          href={url?.replace(replace, id)}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: '14px', fontWeight: '500', color: '#456BD9' }}
        >
          {name}
        </ExternalLink>
      );
    }
    return (
      <Link to="/" onClick={(e) => e.preventDefault()} sx={{ fontSize: '14px', fontWeight: '500', color: '#456BD9' }}>
        {name}
      </Link>
    );
  };

  // fetching records
  const vehicleLabels = useMemo(
    () =>
      metaData?.selectedTab.rows?.[1]?.rows?.map((row: any) => {
        const value = quickGlanceData ? jsonPathToValue(quickGlanceData, row.property) : '';
        return {
          label: row.propertyName,
          title: getTitle(row.type, value),
          id: generateRandomString(10),
          isOptional: row.isOptional,
        };
      }) || [],
    [quickGlanceData, metaData?.selectedTab.rows]
  );

  // fetching records
  const fetchQuickGlanceRecords = useCallback(async () => {
    try {
      const resp = await vehicleService({ method: 'GET', url: `${reservationId}/quickGlance` });
      const copy = { ...resp?.data?.data };
      const addresses = copy?.addresses?.map((item: any) => [item?.country, item?.postalCode]);
      dispatch(setQuickGlanceData({ ...copy, addresses: addresses?.[0]?.toString()?.replace(/,/g, ', ') }));
    } catch (error) {
      console.log(error);
    }
  }, [reservationId, dispatch]);

  // lifecycle hooks
  useEffect(() => {
    fetchQuickGlanceRecords();
  }, [fetchQuickGlanceRecords]);

  if (!metaData || !quickGlanceData) return <QuickGlanceLoader loop={8} />;
  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mt={0}>
        <GridLabels
          labels={vehicleLabels}
          labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
          titleSx={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#3E3E3E',
          }}
        />
        <Grid item xs={12}>
          <Divider sx={{ borderColor: '#E9E9E9' }} />
        </Grid>
        {/* - - teams - - - - */}
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography size={16} weight={500}>
              {quickGlanceMetaData?.subsection?.subHeader}
            </Typography>
            {/* <Link
              to={quickGlanceMetaData?.subsection?.viewlAlll?.link}
              sx={{ fontSize: 14, fontWeight: 400, color: '#456BD9' }}
            >
              {quickGlanceMetaData?.subsection?.viewlAlll?.label}
            </Link> */}
          </Stack>
          <TeamList />
        </Grid>

        {/* - - reference links - - - - */}
        <Grid item xs={12}>
          <Divider sx={{ borderColor: '#E9E9E9' }} />
        </Grid>
        <Grid item xs={12} paddingTop={0}>
          <Box>
            <Typography size={12} weight={500} sx={{ textTransform: 'uppercase' }} textAlign="center" mb={1.5}>
              Reference Links
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-evenly"
              paddingBottom={2}
              divider={<Divider orientation="vertical" sx={{ height: '16px', width: '1px', borderColor: '#D9D9D9' }} />}
            >
              {handleLink(
                'Salesforce',
                'replaceSalesForce',
                data?.delivery?.Delivery_18_Char_ID__c,
                process.env.REACT_APP_SALESFORCE
              )}
              {handleLink('SAP', 'replaceSAP', data?.order?.sapOrderNumber, process.env.REACT_APP_SAP)}
              {handleLink('Lucid Garage', 'replaceVIN', data?.VIN, process.env.REACT_APP_LUCID_GARAGE)}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuickGlance;
